import { handleSwMessage } from './posthogRelay';
import { createIdleReloadScheduler } from './idleReload';

/**
 * P15 — registra o Service Worker em produção.
 *
 * Scope `/app/` (não global) — alinha com P9 D1 (stubs em /, /producer, /admin).
 * Dev (`import.meta.env.PROD === false`) NÃO registra: Vite serve `public/*`
 * com headers que confundem SW (HMR + cache instável). Dev testa SW via
 * `bun run build && bun run preview`.
 *
 * Listener postMessage: SW posta `{type: 'sw-installed', version}` no activate;
 * relay pra PostHog pra observability da distribuição de versões.
 *
 * Wave 5 #56 — `controllerchange` and `bundle-stale` reloads are deferred
 * until the user has been idle for >= IDLE_THRESHOLD_MS. Without that, a
 * deploy landing while the user is mid-input snaps the page out from under
 * the form. We track input/click/touch/scroll/keydown timestamps and poll
 * every IDLE_POLL_MS until the gap is large enough.
 */
const IDLE_THRESHOLD_MS = 30_000;
const IDLE_POLL_MS = 1_000;

export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    try {
      await navigator.serviceWorker.register('/hook-sw.js', { scope: '/app/' });
    } catch (err) {
      console.warn('[hook] SW register failed:', err);
    }
  });

  // Track user activity. We treat any input event as activity. Initial value
  // is `0` so on a freshly-loaded page (no interaction yet) the page counts
  // as idle and a reload fires immediately — first-mount behavior is
  // unchanged from the pre-Wave-5 path.
  let lastActivityAt = 0;
  const bumpActivity = () => {
    lastActivityAt = Date.now();
  };
  if (typeof window !== 'undefined') {
    const events: Array<keyof WindowEventMap> = [
      'pointerdown',
      'mousedown',
      'touchstart',
      'keydown',
      'wheel',
      'scroll',
    ];
    for (const ev of events) {
      window.addEventListener(ev, bumpActivity, { passive: true, capture: true });
    }
  }

  // Shared scheduler — first reason wins; second trigger no-ops. Idle-aware
  // (Wave 5 #56) so a deploy landing mid-input doesn't snap the page out.
  const scheduler = createIdleReloadScheduler({
    idleThresholdMs: IDLE_THRESHOLD_MS,
    pollIntervalMs: IDLE_POLL_MS,
    getLastActivityAt: () => lastActivityAt,
    onReload: (reason) => {
      console.debug(`[hook] reloading for ${reason}`);
      window.location.reload();
    },
  });
  const scheduleReload = (reason: string) => scheduler.schedule(reason);

  navigator.serviceWorker.addEventListener('message', (event) => {
    const msg = event.data as { type?: string } | null;

    // G30 (Solution 2) — SW detected a new bundle deploy via ETag mismatch
    // against its BUNDLE_CACHE. Reload so the tab runs the fresh bundle
    // instead of whatever was already imported via blob URL on first mount.
    // Without this, users on long-lived tabs stay on stale bundle code
    // until they manually close/reopen the tab.
    if (msg?.type === 'bundle-stale') {
      scheduleReload('bundle-stale');
      return;
    }

    const posthog = (globalThis as any).posthog;
    const capture = posthog?.capture?.bind(posthog);
    if (typeof capture === 'function') {
      handleSwMessage(event as MessageEvent, capture);
    }
  });

  // Auto-reload when a newly-activated SW takes control of this page.
  // Without this, a deploy that ships a new SW_VERSION (and new app.js
  // chunks referenced by a new index.html) takes effect only on the
  // next manual navigation — users on a long-lived tab sit on stale
  // controller indefinitely. First install has no previous controller,
  // so this doesn't fire on first visit (correct — nothing to reload).
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    scheduleReload('controllerchange');
  });
}
